import { useEffect, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ApiError, useSelf } from '@/lib/api/queries';

/** The API's 403 code for a CoreFi Account without the Fluentix access role. */
const ACCESS_REQUIRED = 'fluentix_access_required';

/**
 * Gate that bounces unauthenticated users to /login. The login page
 * triggers the Keycloak redirect; once Keycloak round-trips back to
 * `/auth/callback`, react-oidc-context loads the user and the gate
 * lets the original destination render.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Treat auth errors as "please sign in again" rather than leaving the
    // user staring at a spinner forever.
    if (auth.error) {
      console.warn('OIDC error, forcing logout:', auth.error);
    }
  }, [auth.error]);

  if (auth.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Loading session…
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  return <AccessGate>{children}</AccessGate>;
}

/**
 * Anyone can create a CoreFi Account, but Fluentix is only open to accounts
 * granted access (Keycloak role fluentix-api › access). The API answers 403
 * `fluentix_access_required` for the rest; show that plainly instead of a
 * page full of failed requests.
 */
function AccessGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const self = useSelf();

  if (self.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Loading session…
      </div>
    );
  }

  if (self.error instanceof ApiError && self.error.code === ACCESS_REQUIRED) {
    const email = auth.user?.profile.email;
    return (
      <div className="flex h-full items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm space-y-4 rounded-lg border bg-card p-8 text-card-foreground shadow-sm">
          <h1 className="text-xl font-semibold tracking-tight">No access to Fluentix yet</h1>
          <p className="text-sm text-muted-foreground">
            {email ? (
              <>
                You are signed in as <span className="font-medium text-foreground">{email}</span>, but
                this CoreFi Account has not been given access to Fluentix.
              </>
            ) : (
              <>This CoreFi Account has not been given access to Fluentix.</>
            )}{' '}
            Ask your CoreFi contact to enable it, then sign in again.
          </p>
          <Button className="w-full" variant="outline" onClick={() => void auth.signoutRedirect()}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
